import { defineStore } from 'pinia';
import api from '@/api';

export const useProductionOrderStore = defineStore('productionOrder', {
  state: () => ({
    // List state with pagination
    orders: [],
    totalCount: 0,
    pageNumber: 1,
    pageSize: 50,
    totalPages: 0,

    // Single order
    order: null,

    // Order lines
    lines: [],

    // Status lookups
    statuses: [],

    // Loading states
    loading: false,
    linesLoading: false,

    // Error state
    error: null
  }),

  getters: {
    getOrders: (state) => state.orders,
    getOrder: (state) => state.order,
    getLines: (state) => state.lines,
    getStatuses: (state) => state.statuses,
    getPagination: (state) => ({
      totalCount: state.totalCount,
      pageNumber: state.pageNumber,
      pageSize: state.pageSize,
      totalPages: state.totalPages
    }),
    isLoading: (state) => state.loading,
    isLinesLoading: (state) => state.linesLoading,
    getError: (state) => state.error
  },

  actions: {
    /**
     * Fetch production orders with pagination
     * @param {Object} [params]
     * @param {boolean} [params.includeInactive]
     * @param {number} [params.pageNumber]
     * @param {number} [params.pageSize]
     */
    async fetchOrders(params = {}) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.productionOrder.getOrders(params);
        this.orders = resp.data.orders || [];
        this.totalCount = resp.data.totalCount;
        this.pageNumber = resp.data.pageNumber;
        this.pageSize = resp.data.pageSize;
        this.totalPages = resp.data.totalPages;
      } catch (e) {
        this.error = e;
        this.orders = [];
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch single production order by ID
     * @param {number} id
     */
    async fetchOrder(id) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.productionOrder.getOrder(id);
        this.order = resp.data;
      } catch (e) {
        this.error = e;
        this.order = null;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Create new production order
     * @param {Object} payload
     */
    async createOrder(payload) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.productionOrder.createOrder(payload);
        this.order = resp.data;
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Update production order
     * @param {number} id
     * @param {Object} payload
     */
    async updateOrder(id, payload) {
      this.loading = true;
      this.error = null;
      try {
        const resp = await api.productionOrder.updateOrder(id, payload);
        this.order = resp.data;
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Delete production order (soft delete)
     * @param {number} id
     */
    async deleteOrder(id) {
      this.loading = true;
      this.error = null;
      try {
        await api.productionOrder.deleteOrder(id);
        this.order = null;
        this.orders = this.orders.filter(o => o.productionOrderId !== id);
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch order lines
     * @param {number} orderId
     */
    async fetchLines(orderId) {
      this.linesLoading = true;
      this.error = null;
      try {
        const resp = await api.productionOrder.getLines(orderId);
        this.lines = resp.data || [];
      } catch (e) {
        this.error = e;
        this.lines = [];
        throw e;
      } finally {
        this.linesLoading = false;
      }
    },

    /**
     * Create order line
     * @param {number} orderId
     * @param {Object} payload
     */
    async createLine(orderId, payload) {
      this.linesLoading = true;
      this.error = null;
      try {
        const resp = await api.productionOrder.createLine(orderId, payload);
        this.lines.push(resp.data);
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.linesLoading = false;
      }
    },

    /**
     * Update order line
     * @param {number} orderId
     * @param {number} lineId
     * @param {Object} payload
     */
    async updateLine(orderId, lineId, payload) {
      this.linesLoading = true;
      this.error = null;
      try {
        const resp = await api.productionOrder.updateLine(orderId, lineId, payload);
        const index = this.lines.findIndex(l => l.orderLineId === lineId);
        if (index >= 0) {
          this.lines[index] = resp.data;
        }
        return resp.data;
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.linesLoading = false;
      }
    },

    /**
     * Delete order line
     * @param {number} orderId
     * @param {number} lineId
     */
    async deleteLine(orderId, lineId) {
      this.linesLoading = true;
      this.error = null;
      try {
        await api.productionOrder.deleteLine(orderId, lineId);
        this.lines = this.lines.filter(l => l.orderLineId !== lineId);
      } catch (e) {
        this.error = e;
        throw e;
      } finally {
        this.linesLoading = false;
      }
    },

    /**
     * Fetch delete preview for an order (returns data, does not store it)
     * @param {number} id
     */
    async fetchDeletePreview(id) {
      try {
        const resp = await api.productionOrder.getDeletePreview(id);
        return resp.data;
      } catch (e) {
        throw e;
      }
    },

    /**
     * Get a suggested, currently-unused order number
     */
    async fetchNextOrderNumber() {
      const resp = await api.productionOrder.getNextOrderNumber();
      return resp.data.orderNumber;
    },

    /**
     * Fetch status lookups
     */
    async fetchStatuses() {
      this.error = null;
      try {
        const resp = await api.productionOrder.getStatuses();
        this.statuses = resp.data || [];
      } catch (e) {
        this.error = e;
        this.statuses = [];
        throw e;
      }
    },

    // Clear methods
    clearOrders() {
      this.orders = [];
      this.totalCount = 0;
      this.pageNumber = 1;
      this.totalPages = 0;
    },

    clearOrder() {
      this.order = null;
    },

    clearLines() {
      this.lines = [];
    },

    clearStatuses() {
      this.statuses = [];
    },

    clearError() {
      this.error = null;
    },

    clearAll() {
      this.clearOrders();
      this.clearOrder();
      this.clearLines();
      this.clearStatuses();
      this.clearError();
    }
  }
});
